<template>
  <div>
    <div>
      <div class="my-3">
        <h5 class="h5 font-semibold text-lg text-gray-700">
          {{ userStore.user.twoFactorAuthenticationRequired ? '2FA Verification' : 'Verify mobile number' }}
        </h5>
        <div v-if="userStore.user.twoFactorAuthenticationRequired">
          <p class="text-sm text-black">We've sent a code to {{ maskEmail(userStore.user.email) }}</p>
        </div>
        <div v-else>
          <p v-if="generalStore.passwordReset" class="text-black">
            If your number is registered, you will receive an SMS with a verification code.
          </p>
          <p class="text-sm text-black" v-else>We've sent a code to {{ cellNumber }}</p>
          <p class="text-sm text-green-500" v-if="isOtpSentSuccessfully">OTP resent successfully.</p>
        </div>
        <p v-if="errorMessage" class="text-error-700">{{ errorMessage }}</p>
      </div>
    </div>

    <div v-if="isLoading">
      <LayupSpinner class="text-brand-700" />
      <p>
        {{ userStore.user.twoFactorAuthenticationRequired ? 'Verifying 2FA code...' : 'Verifying mobile number...' }}
      </p>
    </div>
    <div class="justify-center" v-else>
      <OtpInput v-model="digitInputs" />
      <TurnstileWidget ref="turnstileRef" @success="handleSuccess" />
      <p v-if="turnstileToken" class="mt-3 text-sm text-black">
        Didn't get a code?
        <a class="text-brand-700 p-0 text-sm cursor-pointer" @click="resendOtp()">Click to resend</a>.
      </p>
    </div>
    <Button
      type="button"
      class="w-full border rounded h-10 w-100 mt-3 disabled:opacity-50 hover:bg-gray-500 hover:text-white text-black"
      @click="router.back()"
      :disabled="isLoading"
    >
      Back
    </Button>
  </div>
</template>

<script setup lang="ts">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import OtpInput from '@/components/common/OtpInput.vue';
import TurnstileWidget from '@/components/common/TurnstileWidget.vue';
import { MERCHANT_UI_URL } from '@/config';
import { NetworkError } from '@/errors';
import type { IUser } from '@/models/users';
import { forgotPassword, otpVerification, resendTwoFactorAuthenticationVerification } from '@/services/auth';
import { useGeneralStore } from '@/stores/general';
import { useMerchantStore } from '@/stores/merchants';
import { useUserStore } from '@/stores/users';
import { hasBetaAccess } from '@/utils/auth.utils';
import { maskEmail } from '@/utils/form.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { ref, toRaw, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const props = defineProps<{
  channel: string;
}>();

const userStore = useUserStore();
const generalStore = useGeneralStore();
const route = useRoute();
const router = useRouter();

const otpValue = ref('');
const errorMessage = ref('');
const isLoading = ref(false);
const isOtpSentSuccessfully = ref(false);
const cellNumber = toRaw(userStore.user.cellNumber);
const digitInputs = ref(['', '', '', '', '']);
const turnstileToken = ref('');
const turnstileRef = ref<InstanceType<typeof TurnstileWidget> | null>(null);
const redirect = route?.query?.redirect as string;

watch(otpValue, async (newOtp) => {
  isOtpSentSuccessfully.value = false;
  if (newOtp.length === 5) {
    await onSubmit();
  }
});

watch(digitInputs, async (newOtp) => {
  otpValue.value = newOtp.join('');
});

const onSubmit = async () => {
  errorMessage.value = '';
  isLoading.value = true;
  if (otpValue.value.length < 5) {
    isLoading.value = false;
    errorMessage.value = 'Invalid otp';
    return;
  }
  try {
    let otpChannel: 'MOBILE' | 'EMAIL';
    let otpChannelId: string;
    if (userStore.user.twoFactorAuthenticationRequired) {
      if (!userStore.user.email) throw new Error('Email required for 2FA.');
      otpChannel = 'EMAIL';
      otpChannelId = userStore.user.email;
    } else {
      if (!userStore.user.cellNumber) throw new Error('Cellphone number is required for verification.');
      otpChannel = 'MOBILE';
      otpChannelId = userStore.user.cellNumber;
    }
    const response = await otpVerification(otpValue.value, otpChannel, otpChannelId);

    userStore.user._id = response._id!;
    userStore.user.name = response.name || '';
    userStore.user.cellNumber = cellNumber;

    addMetric(`otp_verification`, { status: 'success', channel: props.channel });

    // If 2FA is enabled then we know that they have verified by this point so we can redirect them to the dashboard
    if (userStore.user.twoFactorAuthenticationRequired) {
      const merchantStore = useMerchantStore();
      if (!merchantStore.activeMerchantId && userStore.user.merchantIds?.length) {
        merchantStore.activeMerchantId = userStore.user.merchantIds[0];
      }
      if (redirect) {
        router.push(redirect);
      } else if (props.channel === 'merchant') {
        if (hasBetaAccess()) {
          router.push({ name: 'merchantDashboardHome' });
        } else {
          window.location.href = `${MERCHANT_UI_URL}/dashboard/`;
        }
      }
    } else {
      if (generalStore.passwordReset) {
        generalStore.passwordReset = false;
        await router.push({ path: 'create-password' });
      } else {
        await router.push({ path: 'sign-in' });
      }
    }
  } catch (error: any) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric(`otp_verification`, { status: 'network_error', channel: props.channel });
    } else {
      createException(error);
      addMetric(`otp_verification`, { status: 'error', channel: props.channel });
      if (error?.response?.data) {
        errorMessage.value = error.response.data;
      } else {
        errorMessage.value = 'An unexpected error occurred. Please try again.';
      }
    }
  } finally {
    isLoading.value = false;
  }
};

const resendOtp = async () => {
  errorMessage.value = '';
  const { redirectTo } = route.query as { redirectTo?: string };

  try {
    userStore.user.twoFactorAuthenticationRequired
      ? await resendTwoFactorAuthenticationVerification(userStore.user as IUser)
      : await forgotPassword(redirectTo!, cellNumber!, 'MOBILE', turnstileToken.value);

    addMetric(`resend_otp`, { status: 'success', channel: props.channel });
    isOtpSentSuccessfully.value = true;
    turnstileRef.value?.reset();
    turnstileToken.value = '';
  } catch (error: any) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric(`resend_otp`, { status: 'network_error', channel: props.channel });
      errorMessage.value = 'Network Error. Please try again.';
    } else {
      createException(error);
      addMetric(`resend_otp`, { status: 'error', channel: props.channel });
      if (error?.response?.data) {
        errorMessage.value = error.response.data;
      } else {
        errorMessage.value = 'An unexpected error occurred. Please try again.';
      }
    }
  }
  turnstileRef.value?.reset();
  turnstileToken.value = '';
};
function handleSuccess(token: string) {
  turnstileToken.value = token;
}
</script>
